import { useState, useEffect } from 'react';
import { Loop } from './use-loops';
import { useTransition } from '@/components/transitions/TransitionContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

/**
 * Hook for managing smooth transitions between post feed items and detailed views
 * Modified to support in-feed expansion rather than modal overlay
 */
export function usePostTransition() {
  // Track the currently active loop for the expanded view
  const [activeLoop, setActiveLoop] = useState<Loop | null>(null);
  
  // Track whether the detailed post view is open
  const [isPostOpen, setIsPostOpen] = useState(false);
  
  // Track the post ID that is currently expanded in the feed
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  
  // Get the transition context
  const { setActiveItem } = useTransition();
  
  // URL handling for expanded posts
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  /**
   * Opens the detailed post view for a specific loop
   * @param loop The loop to display in detail
   * @param focusComments Whether to focus on the comments section
   */
  const openPost = (loop: Loop, focusComments: boolean = false) => {
    console.log('openPost called with:', loop.id, 'focusComments:', focusComments);
    // Set as active item in the transition context
    setActiveItem({ id: loop.id, data: { ...loop, focusComments } });
    
    // Store the loop and open the post view
    setActiveLoop(loop);
    setIsPostOpen(true);
    
    // Set the expanded post ID for in-feed expansion
    setExpandedPostId(loop.id);
    
    // Update URL with post ID without triggering a navigation
    const params = new URLSearchParams(searchParams);
    params.set('post', loop.id);
    
    // Preserve existing view and order params if they exist
    const viewParam = searchParams.get('view');
    if (viewParam) params.set('view', viewParam);
    
    const orderParam = searchParams.get('order');
    if (orderParam) params.set('order', orderParam);
    
    setSearchParams(params, { replace: true });
    
    // No longer need to prevent scrolling since we're expanding in-feed
    // document.body.style.overflow = 'hidden';
    
    // Scroll the expanded post into view (if needed)
    setTimeout(() => {
      const expandedElement = document.getElementById(`expanded-post-${loop.id}`);
      if (expandedElement) {
        expandedElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  /**
   * Closes the detailed post view
   */
  const closePost = () => {
    // Clear the active item in the transition context
    setActiveItem(null);
    
    // Close the post view
    setIsPostOpen(false);
    
    // Clear the expanded post ID
    setExpandedPostId(null);
    
    // Remove post ID from URL
    const params = new URLSearchParams(searchParams);
    params.delete('post');
    
    // Preserve existing view and order params if they exist
    const viewParam = searchParams.get('view');
    if (viewParam) params.set('view', viewParam);
    
    const orderParam = searchParams.get('order');
    if (orderParam) params.set('order', orderParam);
    
    setSearchParams(params, { replace: true });
    
    // Scroll back to where we were in the feed
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    
    // Clear the active loop after a delay to allow exit animations
    setTimeout(() => {
      setActiveLoop(null);
    }, 300);
  };

  // Handle initial post ID from URL
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId && !expandedPostId) {
      // We have a post ID in URL but no expanded post - need to load that post
      console.log('Found post ID in URL:', postId);
      // Note: This requires a way to fetch a specific post by ID
      // For now, we'll just update the expandedPostId state
      // A complete solution would fetch the post data and then call openPost
      setExpandedPostId(postId);
    }
  }, [searchParams]);

  return {
    activeLoop,
    isPostOpen,
    expandedPostId,
    openPost,
    closePost,
  };
}

export default usePostTransition;
